// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, OnInit } from '@angular/core';
import { HeaderService } from '../../header.service';
import { BehaviorSubject, combineLatest, EMPTY, forkJoin, of } from 'rxjs';
import {
  DASHBOARD_LAST_SEEN_CUTOFF_DAYS, DiskConfigMode,
  IDrive, IProvisionDriveInfo, IProvisionDrivesRequest,
  ISecureSsdDashboardInfo,
  ISecureSsdDriveInfo
} from '../../../types/drive';
import { D3eService } from '../../../services/d3e-service';
import { SelectedOrgMethod } from '../../../shared/selectedOrg';
import { Sort } from '@angular/material/sort';
import { LicenseMethods } from '../../../shared/license.methods';
import {IColumnChooserElement} from '../../shared/components/column-chooser/column-chooser.component';
import { TranslateService } from '@ngx-translate/core';
import { getFullOpalState } from '../../../types/subscription';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {ThemeService} from '../../../services/theme.service';
import authHelper from '../../auth/authHelper';
import { RestConfigService } from '../../../services/config-service';
import { IRestConfig } from '../../../types/config';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from '../../dialogs/alert/alert.component';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { NotificationService } from '../../../services/notification-service';
import { ProvisioningTemplateComponent } from '../../dialogs/provisioning-template/provisioning-template.component';
import { UserService } from '../../../services/user-service';
import { OrgType } from '../../../types/group';
import { AdminSettingsService } from '../../../services/admin-settings.service';
import { IAdminSettings } from '../../../types/admin-settings';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// Enable the UTC plugin
dayjs.extend(utc);

interface IDriveFilter {
  model?: string;
  removable?: boolean;
  configured?: null | number;
  seenInLast7Days?: boolean;
  lastSeenFilter?: number;
}

enum LastSeenFilter {
  ALL_SUBSCRIPTIONS = 0,
  CONNECTED = 15,
  WITHIN_7_DAYS = 7,
  WITHIN_1_MONTH = 30,
  WITHIN_3_MONTHS = 90,
  WITHIN_6_MONTHS = 180
}

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-secure-ssd',
    templateUrl: './secure-ssd.component.html',
    styleUrls: ['./secure-ssd.component.scss'],
    standalone: false
})

export class SecureSsdComponent implements OnInit {
  isMSP$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  adminSettings: IAdminSettings;
  TB = 1099511627776;
  GB = 1073741824;
  MB = 1048576;
  skeletonData = [];
  disconnect_threshold = 900;
  restConfig: IRestConfig;
  selectedOrgId: string;
  columns: IColumnChooserElement[] = [];
  columnsToDisplay: string[] = [];
  sortAttrs = [{ 'sortAttr': 'lastSeen', 'sortOrder': 'desc' }];
  filters: IDriveFilter = {};
  filterValues: string[][] = [];

  searchValue: string = null;

  diskDataLoaded = false;
  drivesLoaded$ = new BehaviorSubject<boolean>(false);
  drives: { checked?: boolean; driveInfo: IDrive} [] = [];
  drivesSelected = false;
  pendingDrivesSelected = false;
  showProvisioningButtons = false;
  pageStart = 0;

  internalCount = 0;
  externalCount = 0;

  dashboardInfo: ISecureSsdDashboardInfo = {
      total: 0,
      internal: 0,
      external: 0,
      seen: 0,
    };
  driveInfo: ISecureSsdDriveInfo[] = [];
  colorScheme = {
    domain: ['#0067B8', '#e3721d', '#808000', '#FA0F00', '#58a252']
  };

  modelChartInfo: any[] = [];
  modelChartStyle = 'fill: black;';
  provisionPendingStateLabel = 'Provision pending';
  selectAllChecked: boolean;
  lastSeenFilter: LastSeenFilter = LastSeenFilter.WITHIN_1_MONTH;
  protected readonly LastSeenFilter = LastSeenFilter;

  totalResults = 0;
  // SECURE SSD FIELDS //
  fInternal: string;
  fExternal: string;
  fDrives: string;
  fDrivesInfo: string;
  fDrivesSerial: string;
  fDrivesModel: string;
  fDrivesCapacity: string;
  fDrivesState: string;
  fDrivesType: string;
  fLastConnected: string;
  fLastSeen: string;
  fConfigured: string;
  fUnconfigured: string;
  fCredsRequired: string;
  fNA: string;
  fConnected: string;
  fTb: string;
  fGb: string;
  fMb: string;
  fTotalSsds: string;
  fLastSeenWidget: string;

  title: string;
  showRansomware: boolean;
  constructor(
      private headerService: HeaderService,
      private d3eService: D3eService,
      private translate: TranslateService,
      private themeService: ThemeService,
      private restConfigService: RestConfigService,
      public dialog: MatDialog,
      private notification: NotificationService,
      private userService: UserService,
      private adminSettingsService: AdminSettingsService,
      private router: Router,
  ) {
    headerService.setContextHelp([
      'help.secure-ssd'
    ]);
    headerService.showRefreshButton(true);
    headerService.refresh$.pipe(untilDestroyed(this)).subscribe(refresh => {
      if (refresh) {
        this.getDrives();
      }
    });

    this.themeService.getTheme()
      .pipe(untilDestroyed(this))
      .subscribe(theme => {
        if (theme === 'dark') {
          this.modelChartStyle = 'fill: white;';
        } else {
          this.modelChartStyle = 'fill: black;';
        }
      });

    this.translate.get(['SSSD_INTERNAL', 'SSSD_EXTERNAL', 'SSSD_DRIVES', 'SSSD_DRIVES_INFO', 'SSSD_SERIAL', 'MODEL',
      'SSSD_CAPACITY', 'SSSD_STATE', 'TYPE', 'SSSD_LAST_C', 'SSSD_LAST_S', 'TITLE_SSD', 'D3ED_CONFIGURED',
      'D3ED_CONNECTED', 'UNCONFIGURED', 'GB', 'MB', 'TB', 'SELECTOR_SECURE_SSDS', 'N_A', 'CREDS_REQUIRED',
      'TOTAL_SSDS']).subscribe(vals => {
      this.fInternal = vals['SSSD_INTERNAL'];
      this.fExternal = vals['SSSD_EXTERNAL'];
      this.fDrives = vals['SSSD_DRIVES'];
      this.fDrivesInfo = vals['SSSD_DRIVES_INFO'];
      this.fDrivesSerial = vals['SSSD_SERIAL'];
      this.fDrivesModel = vals['MODEL'];
      this.fDrivesCapacity = vals['SSSD_CAPACITY'];
      this.fDrivesState = vals['SSSD_STATE'];
      this.fDrivesType = vals['TYPE'];
      this.fLastConnected = vals['SSSD_LAST_C'];
      this.fLastSeen = vals['SSSD_LAST_S'];
      this.fConfigured = vals['D3ED_CONFIGURED'];
      this.fUnconfigured = vals['UNCONFIGURED'];
      this.fCredsRequired = vals['CREDS_REQUIRED'];
      this.fConnected = vals['D3ED_CONNECTED'];
      this.fNA = vals['N_A'];
      this.fTb = vals['TB'];
      this.fGb = vals['GB'];
      this.fMb = vals['MB'];
      this.fTotalSsds = vals['TOTAL_SSDS'];
      this.title = vals['TITLE_SSD'];

      const daycount = DASHBOARD_LAST_SEEN_CUTOFF_DAYS.toString();
      this.fLastSeenWidget = this.translate.instant('DRIVES_LAST_SEEN', {daycount: daycount});

      this.columns = [
        { title: 'select', value: 'checkbox', selected: true, hidden: true, },
        { title: this.fDrivesSerial, value: 'serial', selected: true, hidden: false, },
        { title: this.translate.instant('ssd.disk_config.drive_partner'), value: 'drive_partner', selected: false, hidden: false},
        { title: this.fDrivesModel, value: 'model', selected: true, hidden: false, },
        { title: this.fDrivesCapacity, value: 'capacity', selected: true, hidden: false, },
        { title: this.translate.instant('VERSION'), value: 'version', selected: false, hidden: false, },
        { title: this.fDrivesState, value: 'state', selected: true, hidden: false, },
        { title: this.translate.instant('OPAL_STATE'), value: 'opal_state', selected: false, hidden: false, },
        { title: this.translate.instant('ssd.disk_config.key'), value: 'disk_config_state', selected: true, hidden: false, },
        { title: 'PBA', value: 'pba_info', selected: false, hidden: false },
        { title: this.translate.instant('ssd.disk_config.keep_alive'), value: 'keep_alive', selected: false, hidden: false, },
        { title: this.translate.instant('ssd.disk_config.command_logging'), value: 'command_logging', selected: false, hidden: false, },
        { title: this.translate.instant('ssd.details.ransomware'), value: 'ransomware_detection', selected: false, hidden: false, },
        { title: this.translate.instant('ssd.details.ransomware-sensitivity'), value: 'ransomware_sensitivity', selected: false, hidden: false, },
        { title: this.fDrivesType, value: 'type', selected: true, hidden: false, },
        { title: this.fLastConnected, value: 'lastConnectedTo', selected: true, hidden: false, },
        { title: this.fLastSeen, value: 'lastSeen', selected: true, hidden: false, },
      ];

      this.showProvisioningButtons = authHelper.getFeatureFlagFromToken('spb') || false;
      if (!this.showProvisioningButtons) {
        this.columns.shift();
      }

      this.headerService.setOrgSelectorDefaultOption(vals['SELECTOR_SECURE_SSDS']);

    });
    this.headerService.setTitle(this.title);
    this.headerService.setSubtitle('');
    this.headerService.setIcon('mdi-harddisk');
    this.headerService.setNavMenuText('');
    this.headerService.showUserFilter(true);

    combineLatest([
      this.headerService.orgSelectedChanged,
      this.userService.subscribeToUser(),
      this.restConfigService.getRestConfig()
    ])
    .pipe(
      switchMap(([selectedOrgId, user, restConfig]) => {
        const orgId = selectedOrgId.toLowerCase() === 'all' ? '' : selectedOrgId;
        return forkJoin([of(selectedOrgId), of(user), this.adminSettingsService.getAdminSettings(orgId), of(restConfig)]);
      }),
      untilDestroyed(this),
    ).subscribe(([selectedOrgId, user, adminSettings, restConfig]) => {
        if (user?.org_type.value === OrgType.MSP && selectedOrgId === 'All') {
          this.isMSP$.next(true);
        } else {
          this.isMSP$.next(false);
        }
        this.selectedOrgId = selectedOrgId;
        this.drivesLoaded$.next(false);
        this.filters.lastSeenFilter = this.lastSeenFilter;
        this.getDrives();
        this.adminSettings = adminSettings;
        this.restConfig = restConfig;
        this.filters.lastSeenFilter = LastSeenFilter.WITHIN_1_MONTH;
    });

    this.skeletonData = [{
      name: 'loading SSDs...',
      series: [
        {name: this.fConfigured, value: 0,},
        {name: this.fUnconfigured, value: 0,},
        {name: this.fCredsRequired,value: 0,},
        {name: this.fNA,value: 0,}]},
    ];
  }

  ngOnInit(): void {
    this.showRansomware = authHelper.getFeatureFlagFromToken('ran') ?? true;
    if ( ! this.showRansomware ) {
      for (let [i, column] of this.columns.entries()) {
        if (column.value ===  'ransomware_detection') {
          this.columns.splice(i, 1);
        }
      }
      for (let [i, column] of this.columns.entries()) {
        if (column.value ===  'ransomware_sensitivity') {
          this.columns.splice(i, 1);
        }
      }

    }
  }

  onProvision() {
    if (!this.adminSettings.enable_disk_config) {
      const dialogRef = this.dialog.open(AlertDialogComponent, {
        width: '450px',
        data: {
          title: this.translate.instant("endpoints.enable-secure-drive-password.title"),
          content: this.translate.instant("endpoints.enable-secure-drive-password.content"),
          rejectBtnName: this.translate.instant('CANCEL'),
          acceptBtnName: this.translate.instant("endpoints.enable-secure-drive-password.accept-button"),
          showRejectBtn: true,
        },
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.router.navigate(['admin-settings', 'advanced']).then();
        }
      });

    } else {
      const driveInfos = this.drives
        .filter(o => o.checked)
        .map(e => {
          return {serial: e.driveInfo.serial, licenseId: e.driveInfo.license_id};
      });
      const drives: IProvisionDrivesRequest = {'drives': driveInfos};

      const dialogRef = this.dialog.open(AlertDialogComponent, {
          width: '618px',
          data: {
              title: this.translate.instant('endpoints.provision-dialog.title'),
              content: this.translate.instant('endpoints.provision-dialog.content'),
              additionalContent: [this.translate.instant('endpoints.provision-dialog.additional-content')],
              rejectBtnName: this.translate.instant('CANCEL'),
              acceptBtnName: this.translate.instant('CONFIRM'),
              showRejectBtn: true,
          }
      });
      dialogRef.afterClosed().pipe(
        switchMap(confirmation => {
            if (confirmation) {
                const selectedOrgId = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
                return this.d3eService.provisionDrives(selectedOrgId, drives);
            }
            return EMPTY;
        }),
        tap(() => {
          this.getDrives();
          this.drivesSelected = false;
        })
      ).subscribe(
        () => this.notification.showSuccess(this.translate.instant('SUCCESS')),
        () => this.notification.showError(this.translate.instant('endpoints.provision-error')),
      );
    }

  }

  onCancelProvision() {
      const driveInfos: IProvisionDriveInfo[] = this.drives
        .filter(o => o.checked)
        .map(e => {
          return {serial: e.driveInfo.serial, licenseId: e.driveInfo.license_id};
      });

    const cancelContent = this.translate.instant('endpoints.cancel-provision-dialog.content');
    const dialogRef = this.dialog.open(AlertDialogComponent, {
        width: '500px',
        data: {
            title: this.translate.instant('endpoints.cancel-provision-dialog.title'),
            content: cancelContent,
            rejectBtnName: this.translate.instant('CANCEL'),
            acceptBtnName: this.translate.instant('CONFIRM'),
            showRejectBtn: true,
        }
    });
    dialogRef.afterClosed().pipe(
      switchMap(confirmation => {
          if (confirmation) {
              const selectedOrgId = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
              return this.d3eService.cancelPendingProvisions(selectedOrgId, driveInfos);
          }
          return EMPTY;
      }),
      tap(() => {
        this.lastSeenFilter = LastSeenFilter.WITHIN_1_MONTH;
        this.filters.lastSeenFilter = LastSeenFilter.WITHIN_1_MONTH;
        this.getDrives();
        this.drivesSelected = false;
      })
    ).subscribe(
        () => this.notification.showSuccess(this.translate.instant('SUCCESS')),
        () => this.notification.showError(this.translate.instant('endpoints.cancel-provision-error')),
    );
  }

  onEditTemplate() {
    const selectedOrgId = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    this.d3eService.getProvisioningTemplate(selectedOrgId).pipe(
      switchMap(provisioningTemplate => {
        const dialogRef = this.dialog.open(
          ProvisioningTemplateComponent,
          {
            width: '805px',
            maxHeight: '90vh',
            data: {
              provisioningTemplate: provisioningTemplate,
              selectedOrgId: selectedOrgId
            },
          }
        );
        return dialogRef.afterClosed();
      }),
      switchMap(dialogResult => {
        if (dialogResult) {
          this.notification.showSuccess(this.translate.instant('SUCCESS'));
        }
        return EMPTY;
      }),
      catchError(() => {
        this.notification.showError(this.translate.instant('ERROR_OCCURRED_UNKNOWN'));
        return EMPTY;
      })
    ).subscribe();

  }

  selectAll() {
      if (this.isMSP$.value) {
          return;
      }
      const unconfigured = this.drives.filter(d => d.driveInfo.configurable);
      let checkedCount = this.drives.reduce((a, e) => e.checked ? a + 1 : a, 0);
      if (checkedCount === unconfigured.length) {
          this.drives.forEach(d => {
            if (d.driveInfo.configurable) {
              d.checked = false;
            }
          });
      } else {
          this.drives.forEach(d => {
            if (d.driveInfo.configurable) {
              d.checked = true;
            }
          });
      }
      this.checkSelectAllLater();
  }

  checkSelects() {
      const checkedDrives  = this.drives.filter(d => d.checked);
      this.pendingDrivesSelected = checkedDrives.length > 0 && checkedDrives.every(d => d.driveInfo.has_disk_defaults);
      const unconfigured = this.drives.filter(d => d.driveInfo.configurable);
      this.drivesSelected = checkedDrives.length > 0;
      this.selectAllChecked = checkedDrives.length === unconfigured.length;
  }

  checkSelectAllLater() {
      setTimeout(this.checkSelects.bind(this), 0);
  }

  getQuery() {
    // current pageStart and pageSize are ignored
    return {
      'pageStart': this.pageStart,
      'pageSize': 50,
      'sorts': this.sortAttrs,
      'filters': this.filters,
    };
  }

  getDrives(clearDrivesList: boolean = true) {
    this.setFilterValues();
    this.drivesLoaded$.next(false);
    if (clearDrivesList) {
      this.drives = [];
      this.pageStart = 0;
    }
    this.modelChartInfo = [];
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    const query = this.getQuery();

    this.d3eService.getDrives(selectedOrg, query)
      .pipe(untilDestroyed(this))
      .subscribe( response => {
        this.drivesLoaded$.next(true);
        const now = dayjs().utc();
        const driveInfo: ISecureSsdDriveInfo[] = [];

        const dashboardInfo: ISecureSsdDashboardInfo = {
          total: response.totalDrives,
          internal: response.internalCount,
          external: response.externalCount,
          seen: response.seenInLast7Days,
        };

        const driveList: { driveInfo: IDrive } [] = [];
        const drives = response.drives;

        drives.forEach( drive => {
          if (drive.model !== undefined && drive.model !== null) {
            if ( drive.model.toLowerCase() === 'k2' ) {
              drive.model = 'K2';
            } else if ( drive.model.toLowerCase() === 'fips' ) {
              drive.model = 'FIPS';
            }
          }

          let dInfo = driveInfo.find(obj => {
            return obj.model === drive.model;
          });
          if ( dInfo === null || dInfo === undefined) {
            dInfo = {
              model: drive.model,
              count: 0,
              configuredCount: 0,
              unconfiguredCount: 0,
              unconfiguredTooltip: '',
              na: 0,
              credsRequired: 0
            };
            driveInfo.push(dInfo);
          }
          dInfo.count++;

          if ( drive.opal_state == 6 ) {
            drive.stateDisplay = this.translate.instant('BLOCK_SID_AUTHENTICATION');
            drive.configurable = false;
          } else if ( drive.opal_state == 7 ) {
            drive.stateDisplay = this.translate.instant('opal_state.secondary_pba');
            drive.configurable = false;
          } else if ( drive.configured === null ) {
            drive.stateDisplay = this.fNA;
            dInfo.na++;
          } else if ( drive.configured === 2 ) {
            drive.stateDisplay = this.fCredsRequired;
            dInfo.credsRequired++;
          } else if ( drive.configured === 1 ) {
            dInfo.configuredCount++;
            if (drive.configMode === DiskConfigMode.USER_CONFIGURED) {
              drive.stateDisplay = this.translate.instant('secure-ssd.user-configured');
            } else if (drive.configMode === DiskConfigMode.REMOTE_CONFIGURED) {
              drive.stateDisplay = this.translate.instant('secure-ssd.remote-configured');
            } else {
              drive.stateDisplay = this.fConfigured;
            }
          } else {
            drive.stateDisplay = this.fUnconfigured;
            dInfo.unconfiguredCount++;
            dInfo.unconfiguredTooltip = this.translate.instant('DRIVES_UNCONFIG_TOOLTIP',
                {count: dInfo.unconfiguredCount, model: dInfo.model});
          }

          if ( drive.removable === 1 ) {
            drive.typeDisplay = this.fExternal;
          } else {
            drive.typeDisplay = this.fInternal;
          }

          drive.opal_state_display = getFullOpalState(drive.opal_state, this.translate);

          let pba_display = this.translate.instant('unknown');
          if (drive?.pba_info) {
            const pbaInstalled = drive.pba_info.installed ? this.translate.instant('device.pba.installed') :
              this.translate.instant('device.pba.not-installed');
            pba_display = drive.pba_info.version ? `${pbaInstalled} (v${drive.pba_info.version})` : pbaInstalled;
          }
          drive.pba_display = pba_display;

          drive.ransomware_detection_display = this.translate.instant('N_A');
          if ( drive?.ransomware_detection ) {
            if (drive.ransomware_detection === 1) {
              drive.ransomware_detection_display = this.translate.instant('DISABLED');
            } else {
              drive.ransomware_detection_display = this.translate.instant('ENABLED');
            }
          }

          drive.ransomware_sensitivity_display = this.translate.instant('N_A');
          if ( drive?.ransomware_detection ) {
            if (drive.ransomware_detection === 1) {
              drive.ransomware_sensitivity_display = this.translate.instant('DISABLED');
            } else {
              drive.ransomware_sensitivity_display = '100';
              if ( drive?.ransomwareRawSensitivity ) {
		      // Note: This algorithm is specific to the current AI Model, and may change with
		      // different disk types.  It is also calculated in the back end in ctmc/drive_functions#get_ransomware_sensitivity
		      // and in src/app/pages/d3e-device/d3e-device.component.ts
                 let sensitivity = 102 - drive.ransomwareRawSensitivity;
                 if ( sensitivity > 100 ) {
                    sensitivity = 100;
                 }
                 if ( sensitivity < 1 ) {
                   sensitivity = 1;
                 }
                 drive.ransomware_sensitivity_display = sensitivity.toString();
              }
            }
          }

          drive.keep_alive_display = this.translate.instant('N_A');
          if (drive.keep_alive) {
            drive.keep_alive_display = drive.keep_alive === 1 ?
              this.translate.instant('DISABLED') : this.translate.instant('ENABLED');
          }

          drive.command_logging_display = this.translate.instant('N_A');
          if (drive.command_logging) {
            drive.command_logging_display = drive.command_logging === 1 ?
              this.translate.instant('DISABLED') : this.translate.instant('ENABLED');
          }

          const seconds_since_last_checkin = LicenseMethods.get_seconds(drive.last_seen, now);

          if ( drive.connected === 1 && seconds_since_last_checkin <= this.disconnect_threshold  ) {
              drive.last_seen_display = this.fConnected;
          } else {
            // because we are not connected, show the last time the endpoint checked in
            drive.last_seen_display = drive.last_seen;
          }

          if ( drive.capacity !== undefined && drive.capacity != null ) {
            if (drive.capacity >= this.TB) {
              drive.capacityDisplay = '' + Math.trunc(drive.capacity / this.TB) + ' ' + this.fTb;
            } else if (drive.capacity >= this.GB) {
              drive.capacityDisplay = '' + Math.trunc(drive.capacity / this.GB) + ' ' + this.fGb;
            } else if ( drive.capacity >= this.MB ) {
              drive.capacityDisplay = '' + Math.trunc(drive.capacity / this.MB) + ' ' + this.fMb;
            }
          }

          const drive_row = { checked: !!this.selectAllChecked, driveInfo: drive };
          driveList.push(drive_row);
        });

        this.drives = [...this.drives, ...driveList];
        this.internalCount = response.internalCount;
        this.externalCount = response.externalCount;
        this.totalResults = response.totalDrives;
        this.dashboardInfo = dashboardInfo;
        if (response.drives.length > 0) {
            this.driveInfo = driveInfo.sort((drive1, drive2) => {
              if (drive1.count > drive2.count ) {
                return -1;
              }

              if (drive1.count < drive2.count ) {
                return 1;
              }

              if ( drive1.model > drive2.model ) {
                return 1;
              }

              if ( drive1.model < drive2.model ) {
                return -1;
              }

              return 0;
            });
        }

        this.modelChartInfo = this.driveInfo.slice(0, 10).map(model => ({
          name: model.model,
          series: [
            {
              name: this.fConfigured,
              value: model.configuredCount,
            },
            {
              name: this.fUnconfigured,
              value: model.unconfiguredCount,
            },
            {
              name: this.fCredsRequired,
              value: model.credsRequired
            },
            {
              name: this.fNA,
              value: model.na,
            }
          ]
        }));
      });
  }

  sortColumn(event: Sort) {

    let sort_attr = event.active;
    if ( sort_attr === 'type' ) {
      sort_attr = 'removable';
    } else if ( sort_attr === 'state' ) {
      sort_attr = 'configured';
    }

    const sort = {
      'sortAttr': sort_attr,
      'sortOrder': event.direction
    };


    this.sortAttrs = [];

    if (sort['sortOrder'] === '') {
      sort['sortOrder'] = 'desc';
    }

    this.sortAttrs.push(sort);

    if ( sort_attr !== 'lastSeen' ) {
      const timesortSecondary = {
        'sortAttr': 'lastSeen',
        'sortOrder': sort['sortOrder']
      };
      this.sortAttrs.push(timesortSecondary);
    }

    this.getDrives();
  }

  updateDisplayedColumns(columns: string[]) {
    this.columnsToDisplay = columns;
  }

  modelChartXAxisFormat(val: any): string {
    if (val % 1 > 0) {
      return "";
    }
    return val;
  }

  onLastSeenSelectChanged(lastSeenFilter: LastSeenFilter) {
    this.lastSeenFilter = lastSeenFilter;
    this.filters.lastSeenFilter = lastSeenFilter;
    this.getDrives();
  }

  filterOnRemovable(removable: boolean) {
    this.filters.removable = removable;
    this.getDrives();
  }

  filterOnSeenInLast7Days() {
    this.lastSeenFilter = LastSeenFilter.WITHIN_7_DAYS;
    this.filters.lastSeenFilter = LastSeenFilter.WITHIN_7_DAYS;
    this.getDrives();
  }

  filterOnNothing() {
    this.filters.removable = undefined;
    this.filters.model = undefined;
    this.filters.configured = undefined;
    this.lastSeenFilter = LastSeenFilter.ALL_SUBSCRIPTIONS;
    this.filters.lastSeenFilter = this.lastSeenFilter;
    this.getDrives();
  }

  filterOnChart(event: string | { series: string; label: string; }) {
    if (typeof event === 'string') {
      this.filters.configured = this.configureLabelToValue(event);
    } else {
      this.filters.model = event.series;
      this.filters.configured = this.configureLabelToValue(event.label);
    }
    this.getDrives();
  }

  removeFilter(key: string) {
    this.filters[key] = undefined;
    if (key === 'lastSeenFilter') {
      this.lastSeenFilter = LastSeenFilter.ALL_SUBSCRIPTIONS;
      this.filterValues[key] = this.lastSeenFilter;
    }
    this.getDrives();
  }

  configureLabelToValue(label: string): null | number {
    switch (label) {
      case this.fNA:
        return null;
      case this.fCredsRequired:
        return 2;
      case this.fConfigured:
        return 1;
      case this.fUnconfigured:
        return 0;
      default:
        return undefined;
    }
  }

  configureValueToLabel(value: any): string {
    switch (value) {
      case null:
        return this.fNA;
      case 2:
        return this.fCredsRequired;
      case 1:
        return this.fConfigured;
      case 0:
        return this.fUnconfigured;
      default:
        return "-";
    }
  }

  lastSeenValueToLabel(value: number): string | null {
    switch (value) {
      case 15:
        return 'Connected';
      case 7:
      case 30:
      case 90:
      case 180:
        return  this.translate.instant('DRIVES_LAST_SEEN', {daycount: value});
    }
  }

  getFilterPair(key: string, value: any) {
    switch (key) {
      case "model":
        return [key, value];
      case "removable":
        return [key, value ? "external" : "internal"];
      case "configured":
        return [key, this.configureValueToLabel(value)];
      case "seenInLast7Days":
        return [key, this.fLastSeenWidget];
      case "lastSeenFilter":
        return [key, this.lastSeenValueToLabel(value)];
    }
  }

  setFilterValues() {
    this.filterValues = Object.keys(this.filters)
      .filter(key => this.filters[key] !== undefined)
      .map(key => this.getFilterPair(key, this.filters[key]));
  }

  onScrollHandler() {
    if (this.drivesLoaded$.getValue()) {
        this.pageStart++;
        this.getDrives(false);
    }
  }
}

